<!--
  - SPDX-FileCopyrightText: 2020 Nextcloud GmbH and Nextcloud contributors
  - SPDX-License-Identifier: AGPL-3.0-or-later
-->

<template>
	<li
		class="question"
		:class="{
			'question--editable': !readOnly,
		}"
		:aria-label="t('forms', 'Question number {index}', { index })">
		<!-- Drag handle -->
		<!-- TODO: implement arrow key mapping to reorder question -->
		<div
			v-if="!readOnly"
			class="question__drag-handle"
			:class="{
				'question__drag-handle--shiftup': shiftDragHandle,
			}">
			<NcButton
				ref="buttonUp"
				:aria-label="t('forms', 'Move question up')"
				:disabled="!canMoveUp"
				class="question__drag-handle-button"
				variant="tertiary-no-background"
				@click.stop="onMoveUp">
				<template #icon>
					<NcIconSvgWrapper :svg="IconArrowUp" />
				</template>
			</NcButton>
			<NcIconSvgWrapper :svg="IconDragIndicator" />
			<NcButton
				ref="buttonDown"
				:aria-label="t('forms', 'Move question down')"
				:disabled="!canMoveDown"
				class="question__drag-handle-button"
				variant="tertiary-no-background"
				@click.stop="onMoveDown">
				<template #icon>
					<NcIconSvgWrapper :svg="IconArrowDown" />
				</template>
			</NcButton>
		</div>

		<!-- Header -->
		<div class="question__header">
			<div class="question__header__title">
				<input
					v-if="!readOnly"
					:placeholder="titlePlaceholder"
					:aria-label="
						t('forms', 'Title of question number {index}', {
							index,
						})
					"
					:value="text"
					class="question__header__title__text question__header__title__text__input"
					type="text"
					dir="auto"
					minlength="1"
					:maxlength="maxStringLengths.questionText"
					required
					@input="onTitleChange" />
				<h3
					v-else
					:id="titleId"
					class="question__header__title__text"
					dir="auto">
					{{ computedText }}
				</h3>
				<div
					v-if="!readOnly && !questionValid"
					:title="warningInvalid"
					class="question__header__title__warning"
					tabindex="0">
					<NcIconSvgWrapper :svg="IconAlertCircleOutline" />
				</div>
				<NcActions
					v-if="!readOnly"
					:id="actionsId"
					forceMenu
					placement="bottom-end"
					class="question__header__title__menu">
					<template v-if="isRequired" #icon>
						<IconOverlay>
							<template #overlay>
								<NcIconSvgWrapper :svg="IconAsterisk" inline />
							</template>
							<NcIconSvgWrapper :svg="IconDotsHorizontal" />
						</IconOverlay>
					</template>
					<NcActionCheckbox
						:modelValue="isRequired"
						@update:modelValue="onRequiredChange">
						<!-- TRANSLATORS Making this question necessary to be answered when submitting to a form -->
						{{ t('forms', 'Required') }}
					</NcActionCheckbox>
					<slot name="actions" />
					<NcActionInput
						:label="t('forms', 'Technical name of the question')"
						:labelOutside="false"
						:showTrailingButton="false"
						:modelValue="name"
						@update:modelValue="onNameChange">
						<template #icon>
							<NcIconSvgWrapper :svg="IconIdentifier" />
						</template>
						{{ t('forms', 'Technical name') }}
					</NcActionInput>
					<NcActionButton closeAfterClick @click="onClone">
						<template #icon>
							<NcIconSvgWrapper :svg="IconContentCopy" />
						</template>
						{{ t('forms', 'Copy question') }}
					</NcActionButton>
					<NcActionButton @click="onDelete">
						<template #icon>
							<NcIconSvgWrapper :svg="IconDelete" />
						</template>
						{{ t('forms', 'Delete question') }}
					</NcActionButton>
				</NcActions>
			</div>
			<div
				v-if="hasDescription || !readOnly"
				class="question__header__description">
				<textarea
					v-if="!readOnly"
					ref="descriptionField"
					dir="auto"
					:value="description"
					:placeholder="
						t(
							'forms',
							'Description (formatting using Markdown is supported)',
						)
					"
					:maxlength="maxStringLengths.questionDescription"
					class="question__header__description__input"
					@input="onDescriptionChange" />
				<!-- eslint-disable vue/no-v-html -->
				<div
					v-else
					:id="descriptionId"
					class="question__header__description__output"
					v-html="computedDescription" />
				<!-- eslint-enable vue/no-v-html -->
			</div>
			<NcNoteCard v-if="hasInfo" :id="infoId" type="info">
				{{ infoMessage }}
			</NcNoteCard>
			<NcNoteCard v-if="hasError" :id="errorId" type="error">
				{{ errorMessage }}
			</NcNoteCard>
		</div>

		<!-- Question content -->
		<slot />
		<!-- Insert question menu -->
		<slot name="insert" />
	</li>
</template>

<script lang="ts">
import type { PropType } from 'vue'
import type { MaxStringLengths } from '../../types/Entities.ts'

import IconAsterisk from '@material-symbols/svg-400/outlined/asterisk.svg?raw'
import IconIdentifier from '@material-symbols/svg-400/outlined/badge.svg?raw'
import IconContentCopy from '@material-symbols/svg-400/outlined/content_copy.svg?raw'
import IconDelete from '@material-symbols/svg-400/outlined/delete.svg?raw'
import IconDragIndicator from '@material-symbols/svg-400/outlined/drag_indicator.svg?raw'
import IconAlertCircleOutline from '@material-symbols/svg-400/outlined/error.svg?raw'
import IconArrowDown from '@material-symbols/svg-400/outlined/keyboard_arrow_down.svg?raw'
import IconArrowUp from '@material-symbols/svg-400/outlined/keyboard_arrow_up.svg?raw'
import IconDotsHorizontal from '@material-symbols/svg-400/outlined/more_horiz.svg?raw'
import { t } from '@nextcloud/l10n'
import { computed, defineComponent, inject, nextTick, onMounted, ref } from 'vue'
import NcActionButton from '@nextcloud/vue/components/NcActionButton'
import NcActionCheckbox from '@nextcloud/vue/components/NcActionCheckbox'
import NcActionInput from '@nextcloud/vue/components/NcActionInput'
import NcActions from '@nextcloud/vue/components/NcActions'
import NcButton from '@nextcloud/vue/components/NcButton'
import NcIconSvgWrapper from '@nextcloud/vue/components/NcIconSvgWrapper'
import NcNoteCard from '@nextcloud/vue/components/NcNoteCard'
import IconOverlay from '../Icons/IconOverlay.vue'

export default defineComponent({
	// eslint-disable-next-line vue/multi-word-component-names
	name: 'Question',

	components: {
		NcIconSvgWrapper,
		IconOverlay,
		NcActions,
		NcActionButton,
		NcActionCheckbox,
		NcActionInput,
		NcButton,
		NcNoteCard,
	},

	props: {
		index: {
			type: Number,
			required: true,
		},

		text: {
			type: String,
			required: true,
		},

		titlePlaceholder: {
			type: String,
			required: true,
		},

		description: {
			type: String,
			required: true,
		},

		isRequired: {
			type: Boolean,
			required: true,
		},

		shiftDragHandle: {
			type: Boolean,
			default: false,
		},

		readOnly: {
			type: Boolean,
			default: false,
		},

		maxStringLengths: {
			type: Object as PropType<MaxStringLengths>,
			required: true,
		},

		name: {
			type: String,
			default: '',
		},

		contentValid: {
			type: Boolean,
			// eslint-disable-next-line vue/no-boolean-default
			default: true,
		},

		warningInvalid: {
			type: String,
			default: t('forms', 'This question needs a title!'),
		},

		canMoveDown: {
			type: Boolean,
			default: false,
		},

		canMoveUp: {
			type: Boolean,
			default: false,
		},

		errorMessage: {
			type: String,
			default: null,
		},

		infoMessage: {
			type: String,
			default: null,
		},
	},

	emits: [
		'update:text',
		'update:description',
		'update:name',
		'update:isRequired',
		'moveDown',
		'moveUp',
		'delete',
		'clone',
	],

	setup(props, { emit }) {
		const markdownit = inject<{ render: (value: string) => string } | null>(
			'$markdownit',
			null,
		)
		const descriptionField = ref<HTMLTextAreaElement | null>(null)
		const buttonUp = ref<{ $el?: HTMLElement } | undefined>(undefined)
		const buttonDown = ref<{ $el?: HTMLElement } | undefined>(undefined)

		/**
		 * Extend text with asterisk if question is required
		 */
		const computedText = computed(() => {
			if (props.isRequired) {
				return props.text + ' *'
			}
			return props.text
		})

		const computedDescription = computed(() => {
			if (!markdownit) {
				return props.description
			}
			return markdownit.render(props.description)
		})

		/**
		 * Question valid, if text not empty and content valid
		 *
		 * @return true if question valid
		 */
		const questionValid = computed(() => !!props.text && props.contentValid)
		const actionsId = computed(() => 'q' + props.index + '_actions')
		const titleId = computed(() => 'q' + props.index + '_title')
		const descriptionId = computed(() => 'q' + props.index + '_desc')
		const hasDescription = computed(() => props.description !== '')
		const hasError = computed(() => !!props.errorMessage)
		const hasInfo = computed(() => !!props.infoMessage)
		const errorId = computed(() => `q${props.index}_error`)
		const infoId = computed(() => `q${props.index}_info`)

		const resizeDescription = (): void => {
			// next tick ensures that the textarea is attached to DOM
			nextTick(() => {
				const textarea = descriptionField.value
				if (textarea) {
					textarea.style.cssText = 'height: 0'
					textarea.style.cssText = `height: ${textarea.scrollHeight + 4}px`
				}
			})
		}

		// Ensure description is sized correctly on initial render
		onMounted(() => {
			nextTick(() => resizeDescription())
		})

		const onTitleChange = (event: Event): void => {
			const target = event.target as HTMLInputElement | null
			if (!target) {
				return
			}
			emit('update:text', target.value)
		}

		const onDescriptionChange = (event: Event): void => {
			const target = event.target as HTMLTextAreaElement | null
			if (!target) {
				return
			}
			resizeDescription()
			emit('update:description', target.value)
		}

		const onNameChange = (name: string): void => {
			emit('update:name', name)
		}

		const onRequiredChange = (isRequired: boolean): void => {
			emit('update:isRequired', isRequired)
		}

		/**
		 * Reorder question but keep focus on the button
		 */
		const onMoveDown = (): void => {
			emit('moveDown')
			nextTick(() => {
				buttonDown.value?.$el?.focus()
			})
		}

		const onMoveUp = (): void => {
			emit('moveUp')
			nextTick(() => {
				buttonUp.value?.$el?.focus()
			})
		}

		/**
		 * Delete this question
		 */
		const onDelete = (): void => {
			emit('delete')
		}

		/**
		 * Clone this question
		 */
		const onClone = (): void => {
			emit('clone')
		}

		return {
			buttonUp,
			buttonDown,
			descriptionField,
			IconAsterisk,
			IconAlertCircleOutline,
			IconArrowDown,
			IconArrowUp,
			IconContentCopy,
			IconDelete,
			IconDotsHorizontal,
			IconDragIndicator,
			IconIdentifier,
			t,
			computedText,
			computedDescription,
			questionValid,
			actionsId,
			titleId,
			descriptionId,
			hasDescription,
			hasError,
			hasInfo,
			errorId,
			infoId,
			resizeDescription,
			onTitleChange,
			onDescriptionChange,
			onNameChange,
			onRequiredChange,
			onMoveDown,
			onMoveUp,
			onDelete,
			onClone,
		}
	},
})
</script>

<style lang="scss" scoped>
@use '../../scssmixins/markdownOutput.scss' as *;

.question {
	position: relative;
	display: flex;
	align-items: stretch;
	flex-direction: column;
	justify-content: stretch;
	margin-block-end: 64px;
	padding-inline-start: var(--default-clickable-area);
	user-select: none;
	background-color: var(--color-main-background);

	&--editable {
		padding-inline-start: 56px; // add 12px for the title input box
	}

	> * {
		cursor: pointer;
	}

	&__drag-handle {
		position: absolute;
		display: flex;
		inset-inline-start: var(--default-grid-baseline);
		flex-direction: column;
		justify-content: center;
		gap: 12px;
		width: var(--default-clickable-area);
		height: 100%;
		color: var(--color-text-maxcontrast);
		cursor: grab;

		&-button {
			position: absolute;
			inset-block-start: -9999px;
		}

		// Avoid moving drag-handle due to newAnswer-input on multiple-Questions
		&--shiftup {
			height: calc(100% - var(--default-clickable-area));
		}

		&:hover,
		&:focus,
		&:focus-within {
			color: var(--color-main-text);

			.question__drag-handle-button {
				position: initial;
				inset-block-start: initial;
			}
		}

		&:active {
			cursor: grabbing;
		}

		> * {
			cursor: grab;
		}
	}

	&__title,
	&__content {
		flex: 1 1 100%;
		max-width: 100%;
		padding: 0;
	}

	&__header {
		display: block;
		padding-block-end: 8px;
		align-items: center;
		flex: 1 1 100%;
		justify-content: space-between;
		width: auto;

		&__title {
			display: flex;
			min-height: 44px;

			&__text {
				flex: 1 1 100%;
				font-size: 16px !important;
				padding-block: 10px;
				padding-inline: 0px;
				font-weight: bold;
				margin: auto !important;

				&__input {
					position: relative;
					inset-inline-start: -12px;
					margin-inline-end: -12px !important;
					padding-inline-start: 10px !important;
				}
			}

			&__warning {
				margin-block: auto;
				margin-inline: 8px;
				color: var(--color-error);
			}

			&__menu {
				margin-block: auto;
				margin-inline-end: 12px;
			}
		}

		&__description {
			display: flex;

			&__input {
				margin: 0px;
				min-height: 1.5em;
				border-width: 2px;
				position: relative;
				inset-inline-start: -12px;
				padding-block: 4px;
				padding-inline: 10px;
				resize: none;
			}

			&__input,
			&__output {
				color: var(--color-text-maxcontrast) !important;
				line-height: 1.5em;
				z-index: inherit;
				overflow-wrap: break-word;
				// match with other inputs
				width: calc(100% - var(--default-clickable-area));
			}
			&__output {
				//compensate border
				padding-block: 6px;
				padding-inline: 0;
				// Styling for rendered Output
				@include markdown-output;
			}
		}
	}
}
</style>
